import React, { useState } from "react";
import { Cross, X } from "lucide-react";
import { RxCross2 } from "react-icons/rx";

const defaultFormData = {
  category: "",
  heading: "",
  tagline: "",
  overViewTagLine: "",
  overViewDesc: "",
  overViewOpints: [],
  capabilities: [],
  impact: [],
  metrics: [],
  useCases: [],
  faq: [],
  getStarted: {
    badge: "",
    headline: "",
    subtitle: "",
    steps: [],
  },
  whyChoose: {
    headline: "",
    subtitle: "",
    items: [],
  },
  platformHighlight: {
    headline: "",
    subheadline: "",
    description: "",
    bullets: [],
    stats: [],
  },
  isActive: true,
};

const ProductFormModal = ({ mode, product, onClose, onSave, apiBaseUrl }) => {
  const [formData, setFormData] = useState(product || defaultFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleArrayFieldChange = (fieldName, index, value) => {
    setFormData((prev) => {
      const newArray = [...prev[fieldName]];
      newArray[index] = value;
      return { ...prev, [fieldName]: newArray };
    });
  };

  const handleAddArrayField = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ""],
    }));
  };

  const handleRemoveArrayField = (fieldName, index) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index),
    }));
  };

  const handleMetricChange = (index, field, value) => {
    setFormData((prev) => {
      const newMetrics = [...prev.metrics];
      newMetrics[index] = { ...newMetrics[index], [field]: value };
      return { ...prev, metrics: newMetrics };
    });
  };

  const handleAddMetric = () => {
    setFormData((prev) => ({
      ...prev,
      metrics: [...prev.metrics, { value: "", label: "" }],
    }));
  };

  const handleRemoveMetric = (index) => {
    setFormData((prev) => ({
      ...prev,
      metrics: prev.metrics.filter((_, i) => i !== index),
    }));
  };

  const handleUseCaseChange = (index, field, value) => {
    setFormData((prev) => {
      const newUseCases = [...prev.useCases];
      newUseCases[index] = { ...newUseCases[index], [field]: value };
      return { ...prev, useCases: newUseCases };
    });
  };

  const handleAddUseCase = () => {
    setFormData((prev) => ({
      ...prev,
      useCases: [...prev.useCases, { title: "", description: "" }],
    }));
  };

  const handleRemoveUseCase = (index) => {
    setFormData((prev) => ({
      ...prev,
      useCases: prev.useCases.filter((_, i) => i !== index),
    }));
  };

  const handleFaqChange = (index, field, value) => {
    setFormData((prev) => {
      const newFaq = [...prev.faq];
      newFaq[index] = { ...newFaq[index], [field]: value };
      return { ...prev, faq: newFaq };
    });
  };

  const handleAddFaq = () => {
    setFormData((prev) => ({
      ...prev,
      faq: [...prev.faq, { question: "", answer: "" }],
    }));
  };

  const handleRemoveFaq = (index) => {
    setFormData((prev) => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index),
    }));
  };

  const handleGetStartedStepChange = (index, field, value) => {
    setFormData((prev) => {
      const newSteps = [...prev.getStarted.steps];
      newSteps[index] = { ...newSteps[index], [field]: value };
      return {
        ...prev,
        getStarted: { ...prev.getStarted, steps: newSteps },
      };
    });
  };

  const handleAddGetStartedStep = () => {
    setFormData((prev) => ({
      ...prev,
      getStarted: {
        ...prev.getStarted,
        steps: [...prev.getStarted.steps, { num: "", title: "", desc: "" }],
      },
    }));
  };

  const handleRemoveGetStartedStep = (index) => {
    setFormData((prev) => ({
      ...prev,
      getStarted: {
        ...prev.getStarted,
        steps: prev.getStarted.steps.filter((_, i) => i !== index),
      },
    }));
  };

  const handleWhyChooseItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.whyChoose.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return {
        ...prev,
        whyChoose: { ...prev.whyChoose, items: newItems },
      };
    });
  };

  const handleAddWhyChooseItem = () => {
    setFormData((prev) => ({
      ...prev,
      whyChoose: {
        ...prev.whyChoose,
        items: [
          ...prev.whyChoose.items,
          { title: "", desc: "", variant: "accent" },
        ],
      },
    }));
  };

  const handleRemoveWhyChooseItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      whyChoose: {
        ...prev.whyChoose,
        items: prev.whyChoose.items.filter((_, i) => i !== index),
      },
    }));
  };

  const handlePlatformStatChange = (index, field, value) => {
    setFormData((prev) => {
      const newStats = [...prev.platformHighlight.stats];
      newStats[index] = { ...newStats[index], [field]: value };
      return {
        ...prev,
        platformHighlight: { ...prev.platformHighlight, stats: newStats },
      };
    });
  };

  const handleAddPlatformStat = () => {
    setFormData((prev) => ({
      ...prev,
      platformHighlight: {
        ...prev.platformHighlight,
        stats: [
          ...prev.platformHighlight.stats,
          { value: "", label: "", variant: "accent" },
        ],
      },
    }));
  };

  const handleRemovePlatformStat = (index) => {
    setFormData((prev) => ({
      ...prev,
      platformHighlight: {
        ...prev.platformHighlight,
        stats: prev.platformHighlight.stats.filter((_, i) => i !== index),
      },
    }));
  };

  const submitForm = async () => {
    try {
      setError(null);
      setLoading(true);

      let url, method;
      console.log("first", apiBaseUrl);
      if (mode === "add") {
        url = `${apiBaseUrl}/create`;
        method = "POST";
      } else if (mode === "edit") {
        url = `${apiBaseUrl}/update/${product._id}`;
        method = "PUT";
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Failed to save product");

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-6xl shadow-xl my-8">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === "add" ? "Add New Product" : "Edit Product"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex justify-between items-center bg-red-100 border border-red-400 text-red-700 px-6 py-3 mt-2 border-b">
          <div className="">
            {error}
          </div>
          <div onClick={()=>{ setError(null)}} className="cursor-pointer">
            <RxCross2/>
          </div>
          </div>
        )}

        {/* Modal Content */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="product-Category"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="isActive"
                    value={formData.isActive}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isActive: e.target.value === "true",
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heading *
                </label>
                <input
                  type="text"
                  name="heading"
                  value={formData.heading}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Product Heading"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tagline *
                </label>
                <input
                  type="text"
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Product tagline"
                />
              </div>
            </div>
            
                {/* Metrics */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Metrics
              </h3>
              {formData.metrics.map((metric, idx) => (
                <div key={idx} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Value
                      </label>
                      <input
                        type="text"
                        value={metric.value}
                        onChange={(e) =>
                          handleMetricChange(idx, "value", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 10x"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Label
                      </label>
                      <input
                        type="text"
                        value={metric.label}
                        onChange={(e) =>
                          handleMetricChange(idx, "label", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Faster"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveMetric(idx)}
                    className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg w-full"
                  >
                    Remove Metric
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddMetric}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Metric
              </button>
            </div>

            {/* Overview Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Overview
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overview Tagline
                </label>
                <input
                  type="text"
                  name="overViewTagLine"
                  value={formData.overViewTagLine}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overview Description
                </label>
                <textarea
                  name="overViewDesc"
                  value={formData.overViewDesc}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overview Points
                </label>
                {formData.overViewOpints.map((point, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={point}
                      onChange={(e) =>
                        handleArrayFieldChange(
                          "overViewOpints",
                          idx,
                          e.target.value
                        )
                      }
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Overview point"
                    />
                    <button
                      onClick={() =>
                        handleRemoveArrayField("overViewOpints", idx)
                      }
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => handleAddArrayField("overViewOpints")}
                  className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
                >
                  + Add Point
                </button>
              </div>
            </div>

            {/* Array Fields */}
            {["capabilities", "impact"].map((fieldName) => (
              <div key={fieldName} className="border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
                  {fieldName}
                </h3>
                {formData[fieldName].map((item, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) =>
                        handleArrayFieldChange(fieldName, idx, e.target.value)
                      }
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Enter ${fieldName.slice(0, -1)}`}
                    />
                    <button
                      onClick={() => handleRemoveArrayField(fieldName, idx)}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => handleAddArrayField(fieldName)}
                  className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
                >
                  + Add {fieldName.slice(0, -1)}
                </button>
              </div>
            ))}

            {/* Use Cases */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Use Cases
              </h3>
              {formData.useCases.map((useCase, idx) => (
                <div key={idx} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={useCase.title}
                      onChange={(e) =>
                        handleUseCaseChange(idx, "title", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Use case title"
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={useCase.description}
                      onChange={(e) =>
                        handleUseCaseChange(idx, "description", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveUseCase(idx)}
                    className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg w-full"
                  >
                    Remove Use Case
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddUseCase}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Use Case
              </button>
            </div>

            {/* FAQ */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">FAQ(Optional)</h3>
              {formData.faq.map((faq, idx) => (
                <div key={idx} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question
                    </label>
                    <input
                      type="text"
                      value={faq.question}
                      onChange={(e) =>
                        handleFaqChange(idx, "question", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="FAQ question"
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Answer
                    </label>
                    <textarea
                      value={faq.answer}
                      onChange={(e) =>
                        handleFaqChange(idx, "answer", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveFaq(idx)}
                    className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg w-full"
                  >
                    Remove FAQ
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddFaq}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add FAQ
              </button>
            </div>

            {/* Get Started Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Get Started
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Badge
                  </label>
                  <input
                    type="text"
                    value={formData.getStarted.badge}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        getStarted: {
                          ...prev.getStarted,
                          badge: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Headline
                  </label>
                  <input
                    type="text"
                    value={formData.getStarted.headline}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        getStarted: {
                          ...prev.getStarted,
                          headline: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.getStarted.subtitle}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        getStarted: {
                          ...prev.getStarted,
                          subtitle: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold text-gray-700 mb-2">Steps</h4>
                {formData.getStarted.steps.map((step, idx) => (
                  <div key={idx} className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={step.num}
                        onChange={(e) =>
                          handleGetStartedStepChange(idx, "num", e.target.value)
                        }
                        placeholder="Day One"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={step.title}
                        onChange={(e) =>
                          handleGetStartedStepChange(idx, "title", e.target.value)
                        }
                        placeholder="Step title"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={step.desc}
                        onChange={(e) =>
                          handleGetStartedStepChange(idx, "desc", e.target.value)
                        }
                        placeholder="Step description"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveGetStartedStep(idx)}
                      className="mt-2 px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-600 rounded w-full"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddGetStartedStep}
                  className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg text-sm"
                >
                  + Add Step
                </button>
              </div>
            </div>

            {/* Why Choose Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Why Choose
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Headline
                  </label>
                  <input
                    type="text"
                    value={formData.whyChoose.headline}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        whyChoose: {
                          ...prev.whyChoose,
                          headline: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.whyChoose.subtitle}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        whyChoose: {
                          ...prev.whyChoose,
                          subtitle: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold text-gray-700 mb-2">Items</h4>
                {formData.whyChoose.items.map((item, idx) => (
                  <div key={idx} className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) =>
                          handleWhyChooseItemChange(idx, "title", e.target.value)
                        }
                        placeholder="Item title"
                        className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <select
                        value={item.variant}
                        onChange={(e) =>
                          handleWhyChooseItemChange(
                            idx,
                            "variant",
                            e.target.value
                          )
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="accent">Accent</option>
                        <option value="teal">Teal</option>
                      </select>
                    </div>
                    <textarea
                      value={item.desc}
                      onChange={(e) =>
                        handleWhyChooseItemChange(idx, "desc", e.target.value)
                      }
                      placeholder="Item description"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                    />
                    <button
                      onClick={() => handleRemoveWhyChooseItem(idx)}
                      className="mt-2 px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-600 rounded w-full"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddWhyChooseItem}
                  className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg text-sm"
                >
                  + Add Item
                </button>
              </div>
            </div>

            {/* Platform Highlight Section */}
            <div className="pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Platform Highlight
              </h3>
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Headline
                  </label>
                  <input
                    type="text"
                    value={formData.platformHighlight.headline}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        platformHighlight: {
                          ...prev.platformHighlight,
                          headline: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subheadline
                  </label>
                  <input
                    type="text"
                    value={formData.platformHighlight.subheadline}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        platformHighlight: {
                          ...prev.platformHighlight,
                          subheadline: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.platformHighlight.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        platformHighlight: {
                          ...prev.platformHighlight,
                          description: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold text-gray-700 mb-2">Bullets</h4>
                {formData.platformHighlight.bullets.map((bullet, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={bullet}
                      onChange={(e) => {
                        const newBullets = [...formData.platformHighlight.bullets];
                        newBullets[idx] = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          platformHighlight: {
                            ...prev.platformHighlight,
                            bullets: newBullets,
                          },
                        }));
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Bullet point"
                    />
                    <button
                      onClick={() => {
                        const newBullets = formData.platformHighlight.bullets.filter(
                          (_, i) => i !== idx
                        );
                        setFormData((prev) => ({
                          ...prev,
                          platformHighlight: {
                            ...prev.platformHighlight,
                            bullets: newBullets,
                          },
                        }));
                      }}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      platformHighlight: {
                        ...prev.platformHighlight,
                        bullets: [...prev.platformHighlight.bullets, ""],
                      },
                    }));
                  }}
                  className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg text-sm"
                >
                  + Add Bullet
                </button>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold text-gray-700 mb-2">Stats</h4>
                {formData.platformHighlight.stats.map((stat, idx) => (
                  <div key={idx} className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <input
                        type="text"
                        value={stat.value}
                        onChange={(e) =>
                          handlePlatformStatChange(idx, "value", e.target.value)
                        }
                        placeholder="Value"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={stat.label}
                        onChange={(e) =>
                          handlePlatformStatChange(idx, "label", e.target.value)
                        }
                        placeholder="Label"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <select
                        value={stat.variant}
                        onChange={(e) =>
                          handlePlatformStatChange(
                            idx,
                            "variant",
                            e.target.value
                          )
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="accent">Accent</option>
                        <option value="teal">Teal</option>
                      </select>
                    </div>
                    <button
                      onClick={() => handleRemovePlatformStat(idx)}
                      className="px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-600 rounded w-full"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddPlatformStat}
                  className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg text-sm"
                >
                  + Add Stat
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-4 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={submitForm}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {loading ? "Saving..." : mode === "add" ? "Create Product" : "Update Product"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductFormModal;